import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, nextTick, onMounted, onUnmounted, reactive, Ref, ref } from 'vue';
import { useFonts,  type UserFont } from '@/composables/fonts';
import store from '@/store';
import CSelect, { type CSelectItem, type Primitive } from '@/components/shared/CSelect';
import type { AxiosError } from 'axios';
import type { TextCharacterProvider } from './types';
import CButton from '@/components/shared/CButton';
import CFileInput from '@/components/shared/CFileInput';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal.vue';
import fontManagerService, { DEFAULT_FONT } from '@/utils/fontManagerService';
import { deleteFont } from '@/api/fonts';
import opentype from '@customily/opentype.js';
import { useToaster } from '@/composables/toaster';

interface ConflictErrorResponse {
    type: string;
    errorText: string;
    list: Record<'id' | 'name', string>[];
}


export default /*#__PURE__*/_defineComponent({
  __name: 'FontSelector',
  props: {
    value: null,
    label: { default: 'Select a font' },
    open: { type: Boolean },
    disabled: { type: Boolean },
    appendToBody: { type: Boolean, default: false },
    showFontUpload: { type: Boolean, default: true }
  },
  emits: ["input"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'input', value: CSelectItem<Primitive>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value?: CSelectItem<string>, label: string, open?: boolean, disabled?: boolean, appendToBody: boolean, showFontUpload: boolean };

const toaster = useToaster();

const CONFLICT_CODE = 409;
const generateSafeFontName = fontManagerService.generateSafeFontName;





const panelProvider = inject<TextCharacterProvider | null>('panelUtils', null);

const { uploadUserFont, generateFontName } = useFonts();

const fonts = computed(() =>
    store.state.userFonts.map<CSelectItem<string, UserFont>>((font) => ({
        label: generateFontName(font),
        value: font.Path,
        name: font.Name,
        deletable: !!font.User_Id && font.Path.indexOf(DEFAULT_FONT) == -1,
        object: font
    }))
);

const onOptionSelected = (option?: CSelectItem<Primitive> | Primitive) => {
    emit('input', option as CSelectItem<Primitive>);
};

const isSupported = async (file: File) => {
    const buffer = await file.arrayBuffer();
    const font = opentype.parse(buffer);
    if (!font) {
        throw new Error('The font file is not supported.');
    }
};

const onFontUpload = async ([fontFile]: File[]) => {
    try {
        panelProvider?.showLoader();
        await isSupported(fontFile);
        await uploadUserFont(fontFile);
        await refreshFontList();
    } catch (error) {
        toaster.error(`Error uploading font ${fontFile.name}:  ${(error as Error).message}`);
    } finally {
        panelProvider?.showLoader(false);
    }
};

const modal = reactive({
    title: '',
    description: '',
    actionVerb: '',
    show: false,
    hideCancel: true,
    conflictError: undefined as ConflictErrorResponse | undefined,
    action: null as (() => void) | null
});

const confirmFontDelete = (font: CSelectItem) => {
    modal.conflictError = undefined;
    modal.title = 'Confirmation';
    modal.description = `Are you sure you want to delete the font ${font.label}?`
    modal.action = () => onFontDeleted(font);
    modal.actionVerb = 'Ok';
    modal.hideCancel = false;
    modal.show = true;
}

const onFontDeleted = async (font: CSelectItem) => {
    try {
        panelProvider?.showLoader();
        modal.show = false
        const filename = font.value.substring(font.value.lastIndexOf('/') + 1, font.value.length);
        await deleteFont(filename);
        panelProvider?.fontFileDeleted(font.value);
        await refreshFontList();
    } catch (error) {
        const _error = error as AxiosError;
        modal.title = 'Error deleting the font';
        modal.action = () => toggleModal();
        modal.description = _error.response?.data;
        modal.actionVerb = 'Ok';
        modal.hideCancel = true;
        if (_error.response?.status == CONFLICT_CODE) {
            modal.title = 'Font in use!';
            modal.conflictError = _error.response?.data;
        }
        modal.show = true;
    } finally {
        panelProvider?.showLoader(false);
    }
};

const toggleModal = () => {
    modal.show = !modal.show
};

const searchString = ref('');
const filteredFonts = computed(() =>
    fonts.value.filter((font) => font.label.toLowerCase().includes(searchString.value.toLowerCase()))
);
const fontList: Ref<CSelectItem[]> = ref([]);

const observer = new IntersectionObserver(async ([entry]) => {
    if (!entry.isIntersecting) return;

    const ul = (entry.target as HTMLElement).offsetParent!;
    const scrollTop = ul.scrollTop;

    loadFonts(fontList.value.length - 1 + fontManagerService.maxFontsToDownload);

    await nextTick();
    ul.scrollTop = scrollTop;
});

const loadFonts = async (limit = fontManagerService.maxFontsToDownload) => {
    const startIdx = searchString.value.length ? 0 : fontList.value.length;
    const newFonts = filteredFonts.value.slice(startIdx, limit).map(f => f.object!);
    fontList.value = filteredFonts.value.slice(0, limit);
    await fontManagerService.loadFontChunksToDOM(newFonts);
};

const refreshFontList = async () => {
    await fontManagerService.initFonts(true);
    loadFonts();
}

const selectorBottom: Ref<HTMLElement | null> = ref(null);
const dropOpen = async () => {
    await nextTick();
    if (!selectorBottom.value) return
    observer.observe(selectorBottom.value)
};

const dropClosed = () => {
    observer.disconnect();
};

const onFontSearch = (searchValue: string) => {
    searchString.value = searchValue || '';
    loadFonts();
}

onMounted(() => {
    loadFonts();
});

onUnmounted(() => {
    dropClosed();
});

return { __sfc: true,toaster, CONFLICT_CODE, generateSafeFontName, props, emit, panelProvider, uploadUserFont, generateFontName, fonts, onOptionSelected, isSupported, onFontUpload, modal, confirmFontDelete, onFontDeleted, toggleModal, searchString, filteredFonts, fontList, observer, loadFonts, refreshFontList, selectorBottom, dropOpen, dropClosed, onFontSearch, CSelect, CButton, CFileInput, ConfirmationModal }
}

})